/**
 * ProductContentOverviewTab V2
 *
 * Grid-Übersicht aller Channels mit Content-Preview (wie Bilder-Grid).
 * Zeigt auf einen Blick welche Channels Texte haben.
 *
 * Built with Tailwind CSS v4.1 and V2 components.
 * WCAG 2.1 Level AA + BFSG konform
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Grid3x3, FileText, RefreshCw, Edit } from 'lucide-react';
import { toast } from 'sonner';
import {
  getProductChannelPackages,
  getProductActiveChannels,
  createChannelPackage,
} from '@/lib/api/channel-packages';
import { getContentBlocksForScope } from '@/lib/api/content-blocks';
import { syncDescriptionsFromJTL } from '@/lib/api/descriptions';
import type { ContentBlock, BaseBlockType } from '@/types/content-blocks';
import { parseBlockType, groupBlocksByLanguage } from '@/types/content-blocks';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  Badge,
  Loading,
  EmptyState,
} from '@/components/v2/ui';
import { cn } from '@/lib/utils';

interface ProductContentOverviewTabProps {
  productId: string;
  onEditChannel?: (channelId: string | null) => void;
}

interface ChannelContentPreview {
  channelId: string | null; // null = product global
  channelName: string;
  integrationType?: string;
  contentBlocks: ContentBlock[];
  hasContent: boolean;
  languageCount: number;
  lastUpdated?: string;
  channelPackageId?: string; // undefined = no package exists yet, needs creation
}

export function ProductContentOverviewTab({ productId, onEditChannel }: ProductContentOverviewTabProps) {
  const { t } = useTranslation();
  const [syncingChannelId, setSyncingChannelId] = useState<string | null>(null);

  // Fetch ALL active channels for this product's integration
  const { data: activeChannels, isLoading: isLoadingActiveChannels } = useQuery({
    queryKey: ['product-active-channels', productId],
    queryFn: () => getProductActiveChannels(productId),
  });

  // Fetch channel packages
  const { data: channelPackages, isLoading: isLoadingChannels, refetch: refetchChannelPackages } = useQuery({
    queryKey: ['product-channel-packages', productId],
    queryFn: () => getProductChannelPackages(productId),
  });

  // Fetch product-level content blocks
  const { data: productContent, isLoading: isLoadingProductContent } = useQuery({
    queryKey: ['content-blocks-product', productId],
    queryFn: () => getContentBlocksForScope('product', productId),
  });

  // Fetch content blocks for each channel package
  const channelContentQueries = useQuery({
    queryKey: ['content-blocks-channels', productId, channelPackages?.channel_packages],
    queryFn: async () => {
      if (!channelPackages?.channel_packages) return [];

      const contentPromises = channelPackages.channel_packages.map(async (pkg) => {
        const blocks = await getContentBlocksForScope('channel_package', pkg.id);
        return {
          channelPackageId: pkg.id,
          blocks: blocks.content_blocks || [],
        };
      });

      return Promise.all(contentPromises);
    },
    enabled: !!channelPackages?.channel_packages,
  });

  const isLoading =
    isLoadingActiveChannels ||
    isLoadingChannels ||
    isLoadingProductContent ||
    channelContentQueries.isLoading;

  // Build preview data
  const previews: ChannelContentPreview[] = [];

  // 1. Product global content
  if (productContent?.content_blocks) {
    const grouped = groupBlocksByLanguage(productContent.content_blocks);
    const languageCount = Object.keys(grouped).reduce((acc, baseType) => {
      return Math.max(acc, Object.keys(grouped[baseType as BaseBlockType]).length);
    }, 0);

    previews.push({
      channelId: null,
      channelName: 'Produkt (global)',
      contentBlocks: productContent.content_blocks,
      hasContent: productContent.content_blocks.length > 0,
      languageCount,
      lastUpdated: productContent.content_blocks[0]?.updated_at,
    });
  }

  // 2. Channel-specific content (for ALL active channels)
  // Determine integration type from first available channel package
  const integrationType =
    channelPackages?.channel_packages?.[0]?.channel?.workspace_integrations?.integration_type;

  if (activeChannels?.channels) {
    // Filter nur aktive Channels
    const activeOnly = activeChannels.channels.filter((ch) => ch.is_active);

    activeOnly.forEach((channel) => {
      // Find channel package for this channel (if exists)
      const pkg = channelPackages?.channel_packages?.find(
        (p) => p.channel_id === channel.id
      );

      // Get content blocks for this channel package (if exists)
      const channelContent = pkg
        ? channelContentQueries.data?.find((c) => c.channelPackageId === pkg.id)
        : null;

      const blocks = channelContent?.blocks || [];
      const grouped = groupBlocksByLanguage(blocks);
      const languageCount = Object.keys(grouped).reduce((acc, baseType) => {
        return Math.max(acc, Object.keys(grouped[baseType as BaseBlockType]).length);
      }, 0);

      previews.push({
        channelId: channel.id,
        channelName: channel.channel_name || channel.channel_display_name || '',
        integrationType: integrationType, // All channels have same integration
        contentBlocks: blocks,
        hasContent: blocks.length > 0,
        languageCount,
        lastUpdated: blocks[0]?.updated_at,
        channelPackageId: pkg?.id, // Will be undefined if no package exists yet
      });
    });
  }

  // Handle sync from JTL
  const handleSync = async (channelId: string) => {
    setSyncingChannelId(channelId);
    try {
      await syncDescriptionsFromJTL(productId);
      toast.success(t('toast.success.descriptionsFromJtl'));
      // Invalidate queries to refetch
      channelContentQueries.refetch();
    } catch (error) {
      console.error('Failed to sync descriptions:', error);
      toast.error(t('toast.error.syncFailed'));
    } finally {
      setSyncingChannelId(null);
    }
  };

  // Navigate to content editor (create channel package if needed)
  const handleEdit = async (preview: ChannelContentPreview) => {
    const { channelId, channelPackageId } = preview;

    console.log('[Overview] handleEdit called:', { channelId, channelPackageId, hasContent: preview.hasContent });

    let targetChannelPackageId = channelPackageId;

    // If channel package doesn't exist yet, create it first
    if (channelId && !channelPackageId) {
      console.log('[Overview] Creating channel package...');

      try {
        toast.info(t('toast.info.channelPackageCreating'));

        const result = await createChannelPackage(productId, channelId);
        console.log('[Overview] Create result:', result);

        if (!result.success) {
          throw new Error(result.error || 'Channel Package konnte nicht erstellt werden');
        }

        // Use the newly created channel package ID
        targetChannelPackageId = result.channel_package.id;
        console.log('[Overview] Created channel package ID:', targetChannelPackageId);

        toast.success(t('toast.success.packageCreated'));

        // Refetch channel packages to get the new package in the list
        console.log('[Overview] Refetching channel packages...');
        await refetchChannelPackages();
        console.log('[Overview] Refetch complete');

      } catch (error) {
        console.error('[Overview] Failed to create channel package:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
        toast.error(`Fehler: ${errorMessage}`);
        return; // Don't navigate if creation failed
      }
    }

    // Navigate to editor with CHANNEL PACKAGE ID (not channel ID!)
    console.log('[Overview] Navigating to editor with channel package ID:', targetChannelPackageId);
    if (onEditChannel) {
      onEditChannel(targetChannelPackageId ?? null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loading />
      </div>
    );
  }

  if (previews.length === 0) {
    return (
      <EmptyState
        icon={<FileText className="h-12 w-12" />}
        title="Keine Content Channels"
        description="Erstellen Sie zuerst Channel Packages um channel-spezifische Inhalte zu verwalten."
      />
    );
  }

  return (
    <div className="product-content-overview-tab space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Grid3x3 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {t('products.contentOverview')}
          </h2>
        </div>
        <Badge variant="secondary" className="text-slate-600 dark:text-slate-400">
          {previews.length} {previews.length === 1 ? 'Channel' : 'Channels'}
        </Badge>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {previews.map((preview) => (
          <ContentChannelCard
            key={preview.channelId || 'product'}
            preview={preview}
            onSync={
              preview.integrationType === 'jtl' && preview.channelId
                ? () => handleSync(preview.channelId!)
                : undefined
            }
            onEdit={() => handleEdit(preview)}
            isSyncing={syncingChannelId === preview.channelId}
          />
        ))}
      </div>
    </div>
  );
}

interface ContentChannelCardProps {
  preview: ChannelContentPreview;
  onSync?: () => void;
  onEdit: () => void;
  isSyncing: boolean;
}

function ContentChannelCard({ preview, onSync, onEdit, isSyncing }: ContentChannelCardProps) {
  const { t } = useTranslation();
  const { channelName, integrationType, hasContent, languageCount, contentBlocks, lastUpdated } =
    preview;

  // Extract preview text (first title/description found)
  const previewText = useMemo(() => {
    const extractText = (content: unknown): string | null => {
      if (typeof content === 'string') return content;
      if (typeof content === 'object' && content !== null) {
        // {text: "..."} format
        if ('text' in content) return (content as Record<string, unknown>).text as string;
        // Multi-lingual format: {"de": "...", "en": "..."} - take first non-empty value
        const values = Object.values(content as Record<string, unknown>);
        for (const v of values) {
          if (typeof v === 'string' && v.trim()) return v;
        }
      }
      return null;
    };

    // Try title first
    const titleBlock = contentBlocks.find((block) => {
      const { baseType } = parseBlockType(block.block_type);
      return baseType === 'title';
    });
    const titleText = extractText(titleBlock?.current_version?.content);
    if (titleText) return { type: 'Title', text: titleText };

    // Fallback to description
    const descBlock = contentBlocks.find((block) => {
      const { baseType } = parseBlockType(block.block_type);
      return baseType === 'description';
    });
    const descText = extractText(descBlock?.current_version?.content);
    if (descText) return { type: 'Beschreibung', text: descText };

    return null;
  }, [contentBlocks]);

  const dateStr = lastUpdated
    ? new Date(lastUpdated).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : null;

  return (
    <Card className="flex flex-col h-full">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 truncate">
              {channelName}
            </h3>
            {integrationType && (
              <Badge variant="secondary" size="sm" className="mt-1">
                {integrationType.toUpperCase()}
              </Badge>
            )}
          </div>
          {hasContent ? (
            <Badge variant="success" size="sm" className="shrink-0">
              {languageCount > 1 ? `${languageCount} Sprachen` : 'Vorhanden'}
            </Badge>
          ) : (
            <Badge variant="secondary" size="sm" className="shrink-0">
              Leer
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        {hasContent && previewText ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400 shrink-0" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {previewText.type}
              </span>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3">
              {previewText.text}
            </p>
            {dateStr && (
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-2">
                Aktualisiert: {dateStr}
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-slate-500 dark:text-slate-500 italic">
            Keine Inhalte vorhanden
          </p>
        )}
      </CardContent>

      <CardFooter className="flex gap-2">
        <Button onClick={onEdit} size="sm" variant="outline" className="flex-1">
          <Edit className="h-4 w-4 mr-2" />
          {hasContent ? t('common.edit') : t('common.create')}
        </Button>
        {onSync && (
          <Button
            onClick={onSync}
            size="sm"
            variant="ghost"
            disabled={isSyncing}
            className="shrink-0"
          >
            <RefreshCw className={cn('h-4 w-4', isSyncing && 'animate-spin')} />
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}
